import { API_BASE_URL } from '@synergy/config';
import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack } from '@mui/material';
import { DeleteIcon, HeartIcon, PdfFileIcon } from '@synergy/theme-next';
import { ExtendedMenuIcon } from '@synergy/theme';
import { FormattedMessage } from 'react-intl';
import { MouseEvent, ReactElement, useState } from 'react';
import { PoolButton, PoolConfirmationModal, PoolRow, useIsMobile } from '@synergy/components-next';
import { PoolFormattedMessage } from '@synergy/components-next';
import { useFormikContext } from 'formik';
import { useNavigate } from 'react-router-dom';
import urlJoin from 'url-join';

import { OrderDataFormValues } from './shopping-cart-page';
import { OrderLimitMonth } from '../../components/order-limit/order-limit-month';
import { OrderLimitSingleOrder } from '../../components/order-limit/order-limit-single-order';
import { ROUTES } from '../../utils/routes';
import { StatusIcon } from '../../components/status-icon/status-icon';
import { addCartToWishlist, removeItemsFromShoppingCart } from '../../store/shopping-cart-actions';
import { currencySelector } from '../../store/configuration-selectors';
import { shoppingCartMinimalInfoSelector, shoppingCartSelector } from '../../store/shopping-cart-selectors';
import { useAppDispatch, useAppSelector } from '../../../store/store';

export const TopBar = (): ReactElement | null => {
    const dispatch = useAppDispatch();
    const currency = useAppSelector(currencySelector);
    const cart = useAppSelector(shoppingCartSelector);
    const { totalNetPrice } = useAppSelector(shoppingCartMinimalInfoSelector);
    const [isDeleteCartDialogOpen, toggleDeleteCartDialog] = useState(false);
    const isMobile = useIsMobile();
    const [menuRef, setMenuRef] = useState<null | HTMLElement>(null);
    const isMenuOpen = Boolean(menuRef);
    const formikContext = useFormikContext<OrderDataFormValues>();
    const navigate = useNavigate();

    const handleMenuClick = (event: MouseEvent<HTMLButtonElement>): void => {
        setMenuRef(event.currentTarget);
    };
    const handleMenuClose = (): void => {
        setMenuRef(null);
    };

    const handleDeleteCart = (): void => {
        toggleDeleteCartDialog(true);
        setMenuRef(null);
    };

    const handleDeleteCartConfirm = (): void => {
        dispatch(removeItemsFromShoppingCart());
        toggleDeleteCartDialog(false);
        setMenuRef(null);
        navigate(ROUTES.cart.empty);
    };

    const handleDownloadPdf = async (): Promise<void> => {
        const errors = await formikContext.validateForm();
        if (Object.keys(errors).length > 0) {
            Object.keys(errors).forEach(key => {
                formikContext.setFieldTouched(key, true, true);
            });

            return;
        }
        window.open(urlJoin(API_BASE_URL, '/shopping-cart/pdf'), '_blank');
    };

    const handleAddToWishlist = (): void => {
        dispatch(addCartToWishlist('default'));
        setMenuRef(null);
    };

    return (
        <>
            <PoolConfirmationModal
                open={isDeleteCartDialogOpen}
                title={<FormattedMessage id="app.cart.deleteCart.title" />}
                action={<FormattedMessage id="app.cart.deleteCart.confirm" />}
                onConfirm={handleDeleteCartConfirm}
                onCancel={() => {
                    toggleDeleteCartDialog(false);
                    setMenuRef(null);
                }}
            >
                <PoolFormattedMessage id="app.cart.deleteCart.content" />
            </PoolConfirmationModal>

            <PoolRow
                minHeight={88}
                paddingTop={isMobile ? 2 : 4}
                paddingBottom={2}
                justifyContent="space-between"
                width="100%"
            >
                {!isMobile && (
                    <>
                        <PoolRow gap={3}>
                            {cart?.perOrderOrderLimit !== undefined && (
                                <OrderLimitSingleOrder
                                    limit={cart.perOrderOrderLimit}
                                    currentSum={totalNetPrice}
                                    currency={currency}
                                />
                            )}
                            {cart?.monthlyOrderLimit !== undefined && (
                                <OrderLimitMonth
                                    limit={cart.monthlyOrderLimit}
                                    currentSum={cart.currentMonthlyOrderSum + totalNetPrice}
                                    currency={currency}
                                />
                            )}
                        </PoolRow>
                        <Box>
                            <PoolButton
                                variant="text"
                                color="dark"
                                startIcon={<DeleteIcon />}
                                onClick={handleDeleteCart}
                            >
                                <FormattedMessage id="app.cart.topBar.deleteCart" />
                            </PoolButton>
                            <PoolButton
                                variant="text"
                                color="dark"
                                startIcon={<HeartIcon />}
                                onClick={handleAddToWishlist}
                            >
                                <FormattedMessage id="app.cart.topBar.addAllToWishlist" />
                            </PoolButton>
                            <PoolButton
                                variant="text"
                                color="dark"
                                startIcon={<PdfFileIcon />}
                                onClick={handleDownloadPdf}
                            >
                                <FormattedMessage id="app.cart.topBar.createPdf" />
                            </PoolButton>
                        </Box>
                    </>
                )}

                {isMobile && (
                    <PoolRow justifyContent={'space-between'} width="100%">
                        <Stack gap={2}>
                            {cart?.perOrderOrderLimit !== undefined && (
                                <PoolRow>
                                    {totalNetPrice > cart.perOrderOrderLimit ? (
                                        <StatusIcon fontSize="medium" />
                                    ) : (
                                        <StatusIcon
                                            fontSize="medium"
                                            variant="success"
                                            sx={{ backgroundColor: theme => theme.palette.common.black }}
                                        />
                                    )}
                                    <FormattedMessage id="app.orderLimit.singleOrderLimit" />
                                </PoolRow>
                            )}

                            {cart?.monthlyOrderLimit !== undefined && (
                                <PoolRow>
                                    {cart.currentMonthlyOrderSum + totalNetPrice > cart.monthlyOrderLimit ? (
                                        <StatusIcon fontSize="medium" />
                                    ) : (
                                        <StatusIcon
                                            fontSize="medium"
                                            variant="success"
                                            sx={{ backgroundColor: theme => theme.palette.common.black }}
                                        />
                                    )}
                                    <FormattedMessage id="app.orderLimit.monthlyOrderLimit" />
                                </PoolRow>
                            )}
                        </Stack>

                        <IconButton color="dark" size="medium" onClick={handleMenuClick}>
                            <ExtendedMenuIcon />
                        </IconButton>
                        <Menu anchorEl={menuRef} open={isMenuOpen} onClose={handleMenuClose}>
                            <MenuItem onClick={handleDeleteCart}>
                                <ListItemIcon>
                                    <DeleteIcon />
                                </ListItemIcon>
                                <ListItemText>
                                    <FormattedMessage id="app.cart.topBar.deleteCart" />
                                </ListItemText>
                            </MenuItem>
                            <MenuItem onClick={handleAddToWishlist}>
                                <ListItemIcon>
                                    <HeartIcon />
                                </ListItemIcon>
                                <ListItemText>
                                    <FormattedMessage id="app.cart.topBar.addAllToWishlist" />
                                </ListItemText>
                            </MenuItem>
                            <MenuItem onClick={handleDownloadPdf}>
                                <ListItemIcon>
                                    <PdfFileIcon />
                                </ListItemIcon>
                                <ListItemText>
                                    <FormattedMessage id="app.cart.topBar.createPdf" />
                                </ListItemText>
                            </MenuItem>
                        </Menu>
                    </PoolRow>
                )}
            </PoolRow>
        </>
    );
};
